// ─── Nominatim geocoding utility ─────────────────────────────────────────────
//
//  Nominatim usage policy: https://operations.osmfoundation.org/policies/nominatim/
//    • Max 1 request per second
//    • Provide HTTP Referer identifying your application
//    • Handle 429 Too Many Requests gracefully
//
//  All Nominatim calls in the app go through this module to centralise
//  rate limiting and error handling.

const _APP_REFERER =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';

let _lastRequestAt = 0;

async function _enforceRateLimit() {
  const gap = Date.now() - _lastRequestAt;
  if (gap < 1000) await new Promise(r => setTimeout(r, 1000 - gap));
  _lastRequestAt = Date.now();
}

/**
 * Geocode a query string via Nominatim.
 *
 * @param {string} query  Free-text location query (e.g. "Paris, France")
 * @returns {Promise<Array>}  Nominatim result array — may be empty if not found
 * @throws {Error}  err.status === 429 → rate limited (show friendly message)
 *                  other errors → network / HTTP failure
 */
export async function nominatimSearch(query) {
  await _enforceRateLimit();

  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
    {
      headers: { 'Accept-Language': 'en' },
      referrer: _APP_REFERER,
      referrerPolicy: 'origin',
    }
  );

  if (res.status === 429) {
    const err = new Error(
      'Location search is temporarily unavailable. Please try again in a moment.'
    );
    err.status = 429;
    throw err;
  }

  if (!res.ok) {
    throw new Error(`Nominatim request failed with status ${res.status}`);
  }

  return res.json();
}
